import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Lock } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';

// Setup -> Fees. Generic operator-defined fee catalog (ESS parity):
// any fee, with Name / Amount / Tax Rate / Description. System rows
// (Rent, Balance Forward) are protected. Fees are charged manually to a
// rental or referenced by the late-fee automation.
const blank = { name: '', amount: '', tax_rate: '', description: '' };

const Fees = () => {
  const { facilityId } = useFacility();
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // fee id or 'new'
  const [form, setForm] = useState(blank);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/api/facilities/${facilityId}/fees/`);
      setFees(r.data);
    } catch (e) {
      setError('Could not load fees');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setForm(blank);
    setEditing('new');
    setError('');
  };

  const startEdit = (f) => {
    setForm({
      name: f.name,
      amount: f.amount,
      tax_rate: f.tax_rate ?? '',
      description: f.description ?? '',
    });
    setEditing(f._id);
    setError('');
  };

  const save = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      name: form.name.trim(),
      amount: parseFloat(form.amount),
      tax_rate: form.tax_rate === '' ? null : parseFloat(form.tax_rate),
      description: form.description.trim() || null,
    };
    if (!payload.name || Number.isNaN(payload.amount)) {
      setError('Name and a numeric amount are required');
      return;
    }
    try {
      if (editing === 'new') {
        await apiClient.post(`/api/facilities/${facilityId}/fees/`, payload);
      } else {
        await apiClient.put(
          `/api/facilities/${facilityId}/fees/${editing}`,
          payload,
        );
      }
      setEditing(null);
      await load();
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete fee "${f.name}"?`)) return;
    try {
      await apiClient.delete(`/api/facilities/${facilityId}/fees/${f._id}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';

  return (
    // Rendered embedded inside FeesSettings (Fee Catalog tab). The page H1 +
    // tab strip come from that wrapper, so this view drops its own page title
    // and just renders the description + action bar + table.
    <div>
      <div className="mb-5 flex items-center justify-between">
        <p className="text-[13px] text-slate-400">
          Charge any fee manually to a customer, or reference it from the
          late-fee automation.
        </p>
        <button
          onClick={startNew}
          className="inline-flex items-center gap-1.5 py-2 px-3 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" /> New Fee
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">
          {error}
        </div>
      )}

      {editing && (
        <form
          onSubmit={save}
          className="mb-5 rounded-xl border border-slate-200/80 bg-white p-5 grid grid-cols-1 md:grid-cols-4 gap-3"
        >
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Name
            </label>
            <input
              className={inputCls}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Lock fee"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Amount ($)
            </label>
            <input
              className={inputCls}
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="50.00"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Tax Rate (%)
            </label>
            <input
              className={inputCls}
              type="number"
              step="0.01"
              value={form.tax_rate}
              onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
              placeholder="optional"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Description
            </label>
            <input
              className={inputCls}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="optional"
            />
          </div>
          <div className="md:col-span-4 flex gap-2">
            <button
              type="submit"
              className="py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="py-2 px-4 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-left">
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Amount</th>
              <th className="px-4 py-2.5 font-medium">Tax Rate</th>
              <th className="px-4 py-2.5 font-medium">Description</th>
              <th className="px-4 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : fees.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                  No fees yet
                </td>
              </tr>
            ) : (
              fees.map((f) => (
                <tr
                  key={f._id}
                  className="border-t border-slate-100 text-slate-700"
                >
                  <td className="px-4 py-2.5 font-medium text-slate-900">
                    <span className="inline-flex items-center gap-1.5">
                      {f.is_system && (
                        <Lock className="h-3 w-3 text-slate-400" />
                      )}
                      {f.name}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">${Number(f.amount).toFixed(2)}</td>
                  <td className="px-4 py-2.5">
                    {f.tax_rate != null ? `${f.tax_rate}%` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">
                    {f.description || '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => startEdit(f)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {!f.is_system && (
                        <button
                          onClick={() => remove(f)}
                          className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <p className="mt-3 text-[12px] text-slate-400">
        System fees (Rent, Balance Forward) are used by the billing engine
        and cannot be deleted. Automatic late fees are configured on the
        Late Fees tab.
      </p>
    </div>
  );
};

export default Fees;
